import { useEffect, useRef, useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Upload, Loader2 } from 'lucide-react';
import { supabase } from '../supabase/client';

interface ImageUploadModalProps {
  target: { questionId: string; optionId?: string };
  onClose: () => void;
  onUpload: (imageData: string) => void;
  allowMultiple?: boolean;
}

export default function ImageUploadModal({ target, onClose, onUpload, allowMultiple }: ImageUploadModalProps) {
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const [isCompressing, setIsCompressing] = useState(false);

  // 压缩图片并返回 Blob（不再返回 base64 字符串）
  const compressImageToBlob = async (file: File): Promise<{ blob: Blob; contentType: string }> => {
    return new Promise((resolve, reject) => {
      if (file.type === 'image/bmp') {
        reject(new Error('BMP格式不支持，请转换为JPG/PNG后上传'));
        return;
      }

      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('无法创建canvas'));
        return;
      }

      img.onload = () => {
        let { width, height } = img;
        const maxWidth = 1200;

        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        // 转 webp Blob，目标控制在 512KB 内
        let quality = 0.75;
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('图片压缩失败'));
              return;
            }

            // 如果超过 512KB，逐步降低质量
            if (blob.size > 512 * 1024) {
              quality = 0.6;
              canvas.toBlob(
                (blob2) => {
                  if (!blob2) {
                    reject(new Error('图片压缩失败'));
                    return;
                  }
                  if (blob2.size > 512 * 1024) {
                    quality = 0.4;
                    canvas.toBlob(
                      (blob3) => {
                        if (!blob3) {
                          reject(new Error('图片压缩失败'));
                          return;
                        }
                        resolve({ blob: blob3, contentType: 'image/webp' });
                      },
                      'image/webp',
                      quality
                    );
                  } else {
                    resolve({ blob: blob2, contentType: 'image/webp' });
                  }
                },
                'image/webp',
                quality
              );
            } else {
              resolve({ blob, contentType: 'image/webp' });
            }
          },
          'image/webp',
          quality
        );
      };

      img.onerror = () => reject(new Error('图片加载失败'));

      const reader = new FileReader();
      reader.onload = (e) => { img.src = e.target?.result as string; };
      reader.onerror = () => reject(new Error('文件读取失败'));
      reader.readAsDataURL(file);
    });
  };

  // 上传 Blob 到 storage 桶，返回 public URL
  const uploadToStorage = async (blob: Blob, contentType: string): Promise<string> => {
    const fileName = `paper-editor/${Date.now()}_${Math.random().toString(36).substring(2, 8)}.webp`;
    const { data, error } = await supabase.storage
      .from('paper-images')
      .upload(fileName, blob, {
        contentType,
        upsert: false,
      });

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from('paper-images')
      .getPublicUrl(data.path);

    return publicUrl;
  };

  const processFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('请上传图片文件');
      return;
    }
    if (file.type === 'image/bmp') {
      alert('BMP格式不支持，请转换为JPG/PNG后上传');
      return;
    }

    setIsCompressing(true);
    try {
      // 1. 压缩图片为 Blob
      const { blob, contentType } = await compressImageToBlob(file);
      // 2. 上传到 storage 桶，获取 URL
      const publicUrl = await uploadToStorage(blob, contentType);
      // 3. 回调返回 URL（而非 base64）
      onUpload(publicUrl);
    } catch (error) {
      const msg = error instanceof Error ? error.message : '图片处理失败';
      alert(msg);
    } finally {
      setIsCompressing(false);
    }
  };

  const handlePaste = useCallback((e: ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) imageFiles.push(file);
      }
    }

    if (imageFiles.length > 0) {
      e.preventDefault();
      imageFiles.forEach(processFile);
    }
  }, [onUpload]);

  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  const handleFileSelect = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/jpg,image/png,image/webp,image/gif';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) processFile(file);
    };
    input.click();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl w-[500px] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-800">上传图片</h3>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <X className="w-5 h-5" />
          </motion.button>
        </div>

        <motion.div
          ref={dropZoneRef}
          whileHover={{ borderColor: '#3b82f6', backgroundColor: 'rgba(59, 130, 246, 0.05)' }}
          className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center cursor-pointer transition-colors"
          onClick={!isCompressing ? handleFileSelect : undefined}
        >
          {isCompressing ? (
            <div className="flex flex-col items-center">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-2" />
              <span className="text-gray-600">正在上传处理...</span>
            </div>
          ) : (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <Upload className="w-8 h-8 text-gray-400" />
              </motion.div>
              <p className="text-gray-600 mb-2">
                点击上传或<span className="text-red-500 font-medium">截图粘贴</span>上传
              </p>
              <p className="text-xs text-gray-400">自动压缩至512KB内，转WebP格式并上传到文件存储</p>
            </>
          )}
        </motion.div>

        <div className="flex justify-end gap-3 mt-6">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onClose}
            className="px-5 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            取消
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}
